/**
 * Shared types for the in-memory fake Supabase client.
 *
 * The fake is shape-compatible with the surface of `@supabase/supabase-js`
 * that this app actually touches (audited across ~310 call sites). It is not a
 * complete PostgREST implementation — see README notes in `fakeSupabase.ts`
 * for the semantics that are deliberately approximated.
 */

/** A table row. The snapshot is plain JSON, so values are `unknown`. */
export type Row = Record<string, unknown>;

/** PostgREST-shaped error. The fake almost never produces one. */
export type FakeError = {
  message: string;
  details: string | null;
  hint: string | null;
  code: string;
};

/** What every builder resolves to — matches `PostgrestResponse`. */
export type FakeResult = {
  // `any` on purpose: call sites destructure `data` into their own row types.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data: any;
  error: FakeError | null;
  count: number | null;
  status: number;
  statusText: string;
};

/** Loader contract provided by `src/demo/data` (owned by another agent). */
export type DemoTableLoader = (name: string) => Promise<Row[]>;

export type FakeSupabaseOptions = {
  /** Async loader for a table's snapshot rows. */
  loadTable?: DemoTableLoader;
  /** Names the loader knows about; anything else resolves to `[]`. */
  tableNames?: readonly string[];
  /** Eager tables — wins over `loadTable`. Handy for tests. */
  tables?: Record<string, Row[]>;
};

export const OK_STATUS = 200;
export const OK_STATUS_TEXT = 'OK';
